import { generateColors } from "@mantine/colors-generator";
import { buildFontFamilyString } from "./fontLoader";
import { ThemeTokens, themeTokens as baseTokens } from "./tokens";

type ThemeOverride = {
  primary?: string;
  primary1?: string;
  primary6?: string;
  primary7?: string;
  primary8?: string;
  secondary?: string;
  secondary4?: string;
  accent?: string;
  primaryAlt?: string;
  surface?: string;
  surfaceMuted?: string;
  surfaceSoft?: string;
  textSecondary?: string;
  textMuted?: string;
  borderMuted?: string;
  textStrong?: string;
  black?: string;
  white?: string;
  success?: string;
  successAlt?: string;
  warning?: string;
  danger?: string;
  neutral?: string;
  background?: string;
  premiumAqua?: string;
  premiumPink?: string;
  premiumOrange?: string;
  tooltip?: string;
  // Typography
  fontFamily?: string;
  fontFamilyHeadings?: string;
};

type Tuple10 = [string, string, string, string, string, string, string, string, string, string];

const toTuple = (palette: readonly string[]): Tuple10 => palette as Tuple10;

const fillPaletteWithOverrides = (baseColor: string, overrides: Partial<Record<number, string>>): Tuple10 => {
  const palette = Array.from({ length: 10 }).fill(baseColor) as Tuple10;
  for (const [index, color] of Object.entries(overrides)) {
    const numericIndex = Number(index);
    if (color && numericIndex >= 0 && numericIndex < palette.length) {
      palette[numericIndex] = color;
    }
  }
  return palette;
};

/**
 *
 * @param override
 */
export function mergeTokensWithOverride(override: ThemeOverride | undefined): ThemeTokens {
  if (!override) return baseTokens;

  const overridePrimary = override.primary ?? baseTokens.colors.primary;
  const overridePrimary1 = override.primary1 ?? baseTokens.colors.primary1;
  const overridePrimary6 = override.primary6 ?? baseTokens.colors.primary6;
  const overridePrimary7 = override.primary7 ?? baseTokens.colors.primary7;
  const overridePrimary8 = override.primary8 ?? baseTokens.colors.primary8;
  const overrideSecondary = override.secondary ?? baseTokens.colors.secondary;
  const overrideSecondary4 = override.secondary4 ?? baseTokens.colors.secondary4;

  const colors: ThemeTokens["colors"] = {
    ...baseTokens.colors,
    primary: overridePrimary,
    primary1: overridePrimary1,
    primary6: overridePrimary6,
    primary7: overridePrimary7,
    primary8: overridePrimary8,
    secondary: overrideSecondary,
    secondary4: overrideSecondary4,
    accent: override.accent ?? baseTokens.colors.accent,
    primaryAlt: override.primaryAlt ?? baseTokens.colors.primaryAlt,
    surface: override.surface ?? baseTokens.colors.surface,
    surfaceMuted: override.surfaceMuted ?? baseTokens.colors.surfaceMuted,
    surfaceSoft: override.surfaceSoft ?? baseTokens.colors.surfaceSoft,
    textSecondary: override.textSecondary ?? baseTokens.colors.textSecondary,
    textMuted: override.textMuted ?? baseTokens.colors.textMuted,
    borderMuted: override.borderMuted ?? baseTokens.colors.borderMuted,
    textStrong: override.textStrong ?? baseTokens.colors.textStrong,
    black: override.black ?? baseTokens.colors.black,
    white: override.white ?? baseTokens.colors.white,
    success: override.success ?? baseTokens.colors.success,
    successAlt: override.successAlt ?? baseTokens.colors.successAlt,
    warning: override.warning ?? baseTokens.colors.warning,
    danger: override.danger ?? baseTokens.colors.danger,
    neutral: override.neutral ?? baseTokens.colors.neutral,
    background: override.background ?? baseTokens.colors.background,
    premiumAqua: override.premiumAqua ?? baseTokens.colors.premiumAqua,
    premiumPink: override.premiumPink ?? baseTokens.colors.premiumPink,
    premiumOrange: override.premiumOrange ?? baseTokens.colors.premiumOrange,
    tooltip: override.tooltip ?? baseTokens.colors.tooltip
  };

  const palette: ThemeTokens["palette"] = {
    ...baseTokens.palette,
    primary: toTuple(
      fillPaletteWithOverrides(overridePrimary, {
        1: overridePrimary1,
        6: overridePrimary6,
        7: overridePrimary7,
        8: overridePrimary8
      })
    ),
    secondary: toTuple(fillPaletteWithOverrides(overrideSecondary, { 4: overrideSecondary4 })),
    accent: toTuple(fillPaletteWithOverrides(colors.accent, {})),
    primaryAlt: toTuple(generateColors(colors.primaryAlt)),
    surface: toTuple(generateColors(colors.surface)),
    surfaceMuted: toTuple(generateColors(colors.surfaceMuted)),
    surfaceSoft: toTuple(generateColors(colors.surfaceSoft)),
    textSecondary: toTuple(generateColors(colors.textSecondary)),
    textMuted: toTuple(generateColors(colors.textMuted)),
    borderMuted: toTuple(generateColors(colors.borderMuted)),
    textStrong: toTuple(generateColors(colors.textStrong)),
    black: toTuple(generateColors(colors.black)),
    white: toTuple(generateColors(colors.white)),
    success: toTuple(generateColors(colors.success)),
    successAlt: toTuple(generateColors(colors.successAlt)),
    warning: toTuple(generateColors(colors.warning)),
    danger: toTuple(generateColors(colors.danger)),
    neutral: toTuple(generateColors(colors.neutral)),
    premiumAqua: toTuple(generateColors(colors.premiumAqua)),
    premiumPink: toTuple(generateColors(colors.premiumPink)),
    premiumOrange: toTuple(generateColors(colors.premiumOrange)),
    tooltip: toTuple(fillPaletteWithOverrides(colors.tooltip, {}))
  };

  const gradients: ThemeTokens["gradients"] = {
    primary: {
      from: palette.primary[5],
      to: palette.primary[7],
      deg: 135
    },
    secondary: {
      from: palette.secondary[4],
      to: palette.primary[6],
      deg: 135
    },
    premium: baseTokens.gradients.premium
  };

  const typography: ThemeTokens["typography"] = {
    fontFamily: override.fontFamily ? buildFontFamilyString(override.fontFamily) : baseTokens.typography.fontFamily,
    fontFamilyHeadings: override.fontFamilyHeadings
      ? buildFontFamilyString(override.fontFamilyHeadings)
      : baseTokens.typography.fontFamilyHeadings
  };

  return {
    ...baseTokens,
    colors,
    palette,
    gradients,
    typography
  };
}
